<template>
  <div>
    <Transition name="editor-auth-toast">
      <p v-if="toastText" class="editor-auth-toast">{{ toastText }}</p>
    </Transition>
    <AuthDialogBase
      v-model="open"
      :mode="mode"
      :model="formModel"
      :copy="copy"
      :status-text="statusText"
      :status-is-error="statusIsError"
      :cooldown-seconds="cooldownSeconds"
      :sending-code="sendingCode"
      :submitting="submitting"
      :disable-browser-validation="loginTest"
      :google-login-enabled="googleLoginEnabled && !loginTest"
      :google-client-id="googleClientId"
      :google-icon-src="googleMarkSrc"
      :github-login-enabled="githubLoginEnabled && !loginTest"
      :github-client-id="githubClientId"
      :github-redirect-uri="githubRedirectUri"
      :github-icon-src="githubMarkSrc"
      :wechat-login-enabled="wechatLoginEnabled && !loginTest"
      :wechat-app-id="wechatAppId"
      :wechat-redirect-uri="wechatRedirectUri"
      :request-email-code="onSendCode"
      @send-code-rejected="onSendCodeRejected"
      @update:mode="onModeChange"
      @update:model="onModelChange"
      @submit="onSubmit"
      @google-credential="onGoogleCredential"
      @oauth-session="onOAuthSession"
    />
    <ClickCaptchaDialog
      v-model="captchaOpen"
      :locale="store.locale"
      :captcha-copy="captchaCopy"
      :api-base="authApiBase"
      endpoint="/api/auth/click-captcha"
      @resolved="onCaptchaResolved"
      @cancelled="onCaptchaCancelled"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { mockSessionToken, normalizeTestEmail } from '@imockup/shared-config/auth-test-bypass'
import AuthDialogBase from '../../../../shared/components/AuthDialogBase.vue'
import ClickCaptchaDialog from '../../../../shared/components/ClickCaptchaDialog.vue'
import type { AuthSession } from '../../../../shared/components/authSessionStore'
import { IMOCKUP_LOGIN_TEST } from '@/config/imockupBuildFlags'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { useMockupEditorStore } from '@/stores/mockupEditor'
import { marketingPathForEditorUiLocale } from '@/router/editorUrlLocale'
import { fetchWithBaUserToken } from '../../../../shared/utils/fetchWithBaUserToken'
import { resolveMarketingApiOrigin } from '@/utils/marketingApiOrigin'

type AuthMode = 'login' | 'register' | 'forgot'
type PendingAction = 'sendResetCode'

interface AuthCopy {
  loginTab: string
  registerTab: string
  loginTitle: string
  registerTitle: string
  forgotTitle: string
  tabsAria: string
  close: string
  email: string
  code: string
  password: string
  newPassword: string
  sendCode: string
  submitting: string
  submitLogin: string
  submitRegister: string
  emailInvalid: string
  /** 获取验证码前邮箱为空时的提示 */
  emailMissingForSendCode: string
  codeSent: string
  loginSuccess: string
  registerSuccess: string
  noAccount: string
  goRegister: string
  requestFailed: string
  invalidCode: string
  emailRegistered: string
  userNotFound: string
  passwordInvalid: string
  passwordTooShort: string
  forgotPassword: string
  backToLoginPrefix: string
  backToLogin: string
  submitForgot: string
  resetCodeSent: string
  resetSuccess: string
  showPassword: string
  hidePassword: string
  passwordRuleHint: string
  strengthWeak: string
  strengthMedium: string
  strengthStrong: string
  forgotStepHint: string
  testFillRequired: string
  captchaRequired: string
  captchaCancelled: string
  cooldownSeconds: string
  rememberMe: string
  rememberDaysLabel: string
  rememberDaysTpl: string
  googleDivider: string
  socialDivider: string
  githubButton: string
  wechatButton: string
  wechatPopupTitle: string
}

interface AuthFormModel {
  email: string
  code: string
  password: string
  remember: boolean
  rememberDays: number
}

/** 与 `AuthDialogBase` 的 `AuthDialogModel` 对齐：`remember` / `rememberDays` 可选。 */
type AuthDialogEmittedModel = Pick<AuthFormModel, 'email' | 'code' | 'password'> &
  Partial<Pick<AuthFormModel, 'remember' | 'rememberDays'>>

const props = withDefaults(defineProps<{ modelValue: boolean; defaultMode?: AuthMode }>(), {
  defaultMode: 'login',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const store = useMockupEditorStore()
const { t } = useI18n()
const { session, setSession, clearSession } = useEditorAuth()

/** 与营销站共用：由仓库 `.env.shared` 中 `LOGIN_TEST` 等变量经 Vite `define` 注入。 */
const loginTest = IMOCKUP_LOGIN_TEST

const mode = ref<AuthMode>(props.defaultMode)
const formModel = ref<AuthFormModel>({
  email: '',
  code: '',
  password: '',
  remember: true,
  rememberDays: 30,
})
const statusText = ref('')
const statusIsError = ref(false)
const sendingCode = ref(false)
const submitting = ref(false)
const cooldownSeconds = ref(0)
const toastText = ref('')
const captchaOpen = ref(false)
const pendingAction = ref<PendingAction | null>(null)
let toastTimer: ReturnType<typeof setTimeout> | null = null
let cooldownTimer: ReturnType<typeof setInterval> | null = null

const authApiBase = computed(() => resolveMarketingApiOrigin())

/** 编辑器静态资源 `public/google.png`（与 Vite `base` 对齐）。 */
const googleMarkSrc = computed(() => {
  const raw = String(import.meta.env.BASE_URL || '/')
  const base = raw.endsWith('/') ? raw : `${raw}/`
  return `${base}google.png`
})

const githubMarkSrc = computed(() => {
  const raw = String(import.meta.env.BASE_URL || '/')
  const base = raw.endsWith('/') ? raw : `${raw}/`
  return `${base}GitHub.png`
})

const googleLoginEnabled = ref(false)
const googleClientId = ref('')
const githubLoginEnabled = ref(false)
const githubClientId = ref('')
const githubRedirectUri = ref('')
const wechatLoginEnabled = ref(false)
const wechatAppId = ref('')
const wechatRedirectUri = ref('')

function normalizeWechatRedirectBaseUrl(value: unknown): string {
  const raw = String(value ?? '').trim().replace(/\/$/, '')
  if (!raw) return ''
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`
}

const open = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/**
 * 登录/注册/找回密码与人机验证相关文案（`apps/editor/src/i18n/locales/*` 的 `auth` 节点）。
 */
const copy = computed<AuthCopy>(() => ({
  loginTab: t('auth.loginTab'),
  registerTab: t('auth.registerTab'),
  loginTitle: t('auth.loginTitle'),
  registerTitle: t('auth.registerTitle'),
  forgotTitle: t('auth.forgotTitle'),
  tabsAria: t('auth.tabsAria'),
  close: t('auth.close'),
  email: t('auth.email'),
  code: t('auth.code'),
  password: t('auth.password'),
  newPassword: t('auth.newPassword'),
  sendCode: t('auth.sendCode'),
  submitting: t('auth.submitting'),
  submitLogin: t('auth.submitLogin'),
  submitRegister: t('auth.submitRegister'),
  emailInvalid: t('auth.emailInvalid'),
  emailMissingForSendCode: t('auth.emailMissingForSendCode'),
  codeSent: t('auth.codeSent'),
  loginSuccess: t('auth.loginSuccess'),
  registerSuccess: t('auth.registerSuccess'),
  noAccount: t('auth.noAccount'),
  goRegister: t('auth.goRegister'),
  requestFailed: t('auth.requestFailed'),
  invalidCode: t('auth.invalidCode'),
  emailRegistered: t('auth.emailRegistered'),
  userNotFound: t('auth.userNotFound'),
  passwordInvalid: t('auth.passwordInvalid'),
  passwordTooShort: t('auth.passwordTooShort'),
  forgotPassword: t('auth.forgotPassword'),
  backToLoginPrefix: t('auth.backToLoginPrefix'),
  backToLogin: t('auth.backToLogin'),
  submitForgot: t('auth.submitForgot'),
  resetCodeSent: t('auth.resetCodeSent'),
  resetSuccess: t('auth.resetSuccess'),
  showPassword: t('auth.showPassword'),
  hidePassword: t('auth.hidePassword'),
  passwordRuleHint: t('auth.passwordRuleHint'),
  strengthWeak: t('auth.strengthWeak'),
  strengthMedium: t('auth.strengthMedium'),
  strengthStrong: t('auth.strengthStrong'),
  forgotStepHint: t('auth.forgotStepHint'),
  testFillRequired: t('auth.testFillRequired'),
  captchaRequired: t('auth.captchaRequired'),
  captchaCancelled: t('auth.captchaCancelled'),
  cooldownSeconds: t('auth.cooldownSeconds'),
  rememberMe: t('auth.rememberMe'),
  rememberDaysLabel: t('auth.rememberDaysLabel'),
  rememberDaysTpl: t('auth.rememberDaysTpl'),
  googleDivider: t('auth.googleDivider'),
  socialDivider: t('auth.googleDivider'),
  githubButton: t('auth.githubButton'),
  wechatButton: t('auth.wechatButton'),
  wechatPopupTitle: t('auth.wechatPopupTitle'),
}))

const captchaCopy = computed(() => ({
  title: t('auth.captcha.title'),
  tipPrefix: t('auth.captcha.tipPrefix'),
  refresh: t('auth.captcha.refresh'),
  undo: t('auth.captcha.undo'),
  confirm: t('auth.captcha.confirm'),
  close: t('auth.captcha.close'),
  loading: t('auth.captcha.loading'),
  stageAlt: t('auth.captcha.stageAlt'),
  loadError: t('auth.captcha.loadError'),
}))

watch(
  () => props.defaultMode,
  (next) => {
    mode.value = next
  },
)

watch(
  () => props.modelValue,
  async (opened) => {
    if (opened) {
      mode.value = props.defaultMode
      resetStatus()
      const base = authApiBase.value
      if (!loginTest && base) {
        try {
          const res = await fetch(`${base}/api/auth/social-config`, { credentials: 'include' })
          if (res.ok) {
            const cfg = (await res.json()) as {
              googleLoginEnabled?: boolean
              googleClientId?: string
              githubLoginEnabled?: boolean
              githubClientId?: string
              wechatLoginEnabled?: boolean
              wechatAppId?: string
              wechatRedirectBaseUrl?: string
            }
            googleLoginEnabled.value = Boolean(cfg?.googleLoginEnabled && cfg?.googleClientId)
            googleClientId.value = String(cfg?.googleClientId ?? '')
            githubLoginEnabled.value = Boolean(cfg?.githubLoginEnabled && cfg?.githubClientId)
            githubClientId.value = String(cfg?.githubClientId ?? '')
            wechatLoginEnabled.value = Boolean(cfg?.wechatLoginEnabled && cfg?.wechatAppId && cfg?.wechatRedirectBaseUrl)
            wechatAppId.value = String(cfg?.wechatAppId ?? '')
            const origin = globalThis.location.origin
            const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
            const path = marketingPathForEditorUiLocale('/oauth/github/callback', store.locale)
            githubRedirectUri.value = `${origin}${base}${path}`
            const wechatBase = normalizeWechatRedirectBaseUrl(cfg?.wechatRedirectBaseUrl)
            const wechatPath = marketingPathForEditorUiLocale('/oauth/wechat/callback', store.locale)
            wechatRedirectUri.value = wechatBase ? `${wechatBase}${base}${wechatPath}` : ''
          } else {
            googleLoginEnabled.value = false
            googleClientId.value = ''
            githubLoginEnabled.value = false
            githubClientId.value = ''
            githubRedirectUri.value = ''
            wechatLoginEnabled.value = false
            wechatAppId.value = ''
            wechatRedirectUri.value = ''
          }
        } catch {
          googleLoginEnabled.value = false
          googleClientId.value = ''
          githubLoginEnabled.value = false
          githubClientId.value = ''
          githubRedirectUri.value = ''
          wechatLoginEnabled.value = false
          wechatAppId.value = ''
          wechatRedirectUri.value = ''
        }
      } else {
        googleLoginEnabled.value = false
        googleClientId.value = ''
        githubLoginEnabled.value = false
        githubClientId.value = ''
        githubRedirectUri.value = ''
        wechatLoginEnabled.value = false
        wechatAppId.value = ''
        wechatRedirectUri.value = ''
      }
      return
    }
    stopCooldown()
    pendingAction.value = null
  },
)

onBeforeUnmount(() => {
  stopCooldown()
  stopToast()
})

/**
 * 切换登录/注册模式并重置提示。
 *
 * @param nextMode - 目标模式
 */
function onModeChange(nextMode: AuthMode): void {
  mode.value = nextMode
  resetStatus()
}

/**
 * 响应基础弹窗字段变更。
 *
 * @param next - 最新表单模型（子组件可能省略 `remember` / `rememberDays`，沿用当前值）
 */
function onModelChange(next: AuthDialogEmittedModel): void {
  formModel.value = {
    email: next.email,
    code: next.code,
    password: next.password,
    remember: true,
    rememberDays: 30,
  }
}

/**
 * 清理状态提示。
 */
function resetStatus(): void {
  statusText.value = ''
  statusIsError.value = false
}

/**
 * 显示顶部 Toast 提示。
 *
 * @param message - 提示文案
 */
function showToast(message: string): void {
  stopToast()
  toastText.value = message
  toastTimer = setTimeout(() => {
    toastText.value = ''
    toastTimer = null
  }, 2200)
}

/**
 * 清理 Toast 定时器与文案。
 */
function stopToast(): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
    toastTimer = null
  }
  toastText.value = ''
}

/**
 * 邮箱格式校验。
 *
 * @param value - 邮箱值
 */
function isEmailValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

/**
 * 测试模式下是否已填写当前模式所需字段。
 *
 * @param authMode - 当前认证模式
 */
function isFilledForLoginTest(authMode: AuthMode): boolean {
  const emailOk = formModel.value.email.trim().length > 0
  const passwordOk = formModel.value.password.length > 0
  if (authMode === 'login') return emailOk && passwordOk
  if (authMode === 'register' || authMode === 'forgot') {
    return emailOk && passwordOk && formModel.value.code.trim().length > 0
  }
  return false
}

/**
 * 根据当前「记住密码」勾选态派生会话有效期字段。
 *
 * - 未勾选：返回 `{}`，使用浏览器默认会话语义
 * - 勾选：以当前时间 + `rememberDays` 天作为过期时间
 */
function resolveSessionKeepFields(): Pick<AuthSession, 'keepDays' | 'expiresAt'> {
  const days = [7, 14, 30].includes(formModel.value.rememberDays) ? formModel.value.rememberDays : 30
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
  return { keepDays: days, expiresAt: expires }
}

/**
 * 测试模式下写入编辑器会话并关闭弹窗。
 *
 * @param toastMessage - Toast 文案
 */
function applyTestSession(toastMessage: string): void {
  const email = normalizeTestEmail(formModel.value.email)
  setSession({
    token: `lt-${mockSessionToken()}`,
    email,
    createdAt: new Date().toISOString(),
    isSubscribed: false,
    ...resolveSessionKeepFields(),
  })
  statusIsError.value = false
  statusText.value = toastMessage
  showToast(toastMessage)
  setTimeout(() => emit('update:modelValue', false), 400)
}

/**
 * 启动验证码倒计时。
 *
 * @param seconds - 倒计时秒数
 */
function startCooldown(seconds: number): void {
  stopCooldown()
  cooldownSeconds.value = seconds
  cooldownTimer = setInterval(() => {
    cooldownSeconds.value -= 1
    if (cooldownSeconds.value <= 0) stopCooldown()
  }, 1000)
}

/**
 * 停止验证码倒计时。
 */
function stopCooldown(): void {
  if (!cooldownTimer) {
    cooldownSeconds.value = 0
    return
  }
  clearInterval(cooldownTimer)
  cooldownTimer = null
  cooldownSeconds.value = 0
}

/**
 * 发起找回密码发送验证码前的点选验证码挑战。
 *
 * @param action - 挂起动作
 */
function challengeCaptcha(action: PendingAction): void {
  pendingAction.value = action
  captchaOpen.value = true
}

/**
 * 子组件在发码前预校验失败时更新状态栏。
 *
 * @param reason - 空邮箱或格式不合法
 */
function onSendCodeRejected(reason: 'empty_email' | 'invalid_email'): void {
  statusIsError.value = true
  statusText.value = reason === 'invalid_email' ? copy.value.emailInvalid : copy.value.emailMissingForSendCode
}

/**
 * 发送验证码：
 *
 * - 注册：直接调用后端发码（不再要求人机验证）
 * - 找回密码：先通过人机验证
 */
async function onSendCode(): Promise<void> {
  if (sendingCode.value || cooldownSeconds.value > 0) return
  if (loginTest) {
    if (!formModel.value.email.trim()) {
      statusIsError.value = true
      statusText.value = copy.value.testFillRequired
      return
    }
    statusIsError.value = false
    statusText.value = mode.value === 'forgot' ? copy.value.resetCodeSent : copy.value.codeSent
    startCooldown(60)
    return
  }
  if (!isEmailValid(formModel.value.email)) {
    statusIsError.value = true
    statusText.value = copy.value.emailInvalid
    return
  }
  if (mode.value === 'forgot') {
    challengeCaptcha('sendResetCode')
    return
  }
  await performSendCode()
}

/**
 * 提交登录或注册请求。
 */
async function onSubmit(): Promise<void> {
  if (submitting.value) return

  if (loginTest) {
    if (!isFilledForLoginTest(mode.value)) {
      statusIsError.value = true
      statusText.value = copy.value.testFillRequired
      return
    }
    submitting.value = true
    statusText.value = ''
    try {
      if (mode.value === 'register') {
        applyTestSession(copy.value.registerSuccess)
        return
      }
      if (mode.value === 'forgot') {
        statusIsError.value = false
        statusText.value = copy.value.resetSuccess
        showToast(copy.value.resetSuccess)
        onModeChange('login')
        return
      }
      applyTestSession(copy.value.loginSuccess)
    } finally {
      submitting.value = false
    }
    return
  }

  if (!isEmailValid(formModel.value.email)) {
    statusIsError.value = true
    statusText.value = copy.value.emailInvalid
    return
  }
  if (mode.value === 'login') {
    await performLogin()
    return
  }
  if (mode.value === 'register') {
    await performRegister()
    return
  }
  if (mode.value === 'forgot') {
    await performResetPassword()
  }
}

/**
 * 用户完成点选验证码的回调：执行找回密码发码动作。
 *
 * @param payload - 点选验证码结果
 */
async function onCaptchaResolved(payload: { captchaId: string; captchaInfo: string }): Promise<void> {
  const action = pendingAction.value
  pendingAction.value = null
  if (!action) return
  if (action === 'sendResetCode') return performSendCode(payload, true)
}

/**
 * 点选验证码被用户取消。
 */
function onCaptchaCancelled(): void {
  if (!pendingAction.value) return
  pendingAction.value = null
  statusIsError.value = true
  statusText.value = copy.value.captchaCancelled
}

/**
 * 统一 fetch JSON 封装，将 `statusText` 抛回给 `resolveErrorMessage`。
 *
 * @param path - 相对路径（如 `/api/auth/login`）
 * @param body - POST body
 */
async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetchWithBaUserToken(
    `${authApiBase.value}${path}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
    session.value?.token,
  )
  if (!res.ok) {
    let message = `request_failed:${res.status}`
    try {
      const data = (await res.json()) as { statusMessage?: string; message?: string }
      const m = data.statusMessage || data.message
      if (m) message = m
    } catch {
      try {
        const t = await res.text()
        if (t) message = t
      } catch {
        /* 保持默认 message */
      }
    }
    throw new Error(message)
  }
  return (await res.json()) as T
}

/**
 * 调用后端发送邮箱验证码。
 *
 * @param payload - 点选验证码结果
 * @param forReset - 是否为找回密码
 */
async function performSendCode(
  payload?: { captchaId: string; captchaInfo: string },
  forReset = false,
): Promise<void> {
  sendingCode.value = true
  statusText.value = ''
  try {
    const endpoint = forReset ? '/api/auth/send-reset-code' : '/api/auth/send-code'
    const requestBody: Record<string, string> = { email: formModel.value.email }
    if (payload?.captchaId && payload?.captchaInfo) {
      requestBody.captchaId = payload.captchaId
      requestBody.captchaInfo = payload.captchaInfo
    }
    await postJson(endpoint, requestBody)
    statusIsError.value = false
    statusText.value = forReset ? copy.value.resetCodeSent : copy.value.codeSent
    showToast(statusText.value)
    startCooldown(60)
  } catch (error) {
    statusIsError.value = true
    statusText.value = resolveErrorMessage(error)
  } finally {
    sendingCode.value = false
  }
}

/**
 * 调用后端登录接口（免点选验证码）。
 */
async function performLogin(): Promise<void> {
  submitting.value = true
  statusText.value = ''
  try {
    const data = await postJson<{
      ok: boolean
      token: string
      refreshToken: string
      email: string
      username: string
      nickname: string
      avatar: string
      createdAt: string
      isSubscribed: boolean
    }>('/api/auth/login', {
      email: formModel.value.email,
      password: formModel.value.password,
      keep: true,
      keepDays: 30,
    })
    setSession({
      token: data.token,
      refreshToken: data.refreshToken,
      email: data.email,
      username: data.username,
      nickname: data.nickname,
      avatar: data.avatar,
      createdAt: data.createdAt,
      isSubscribed: data.isSubscribed,
      ...resolveSessionKeepFields(),
    })
    statusText.value = copy.value.loginSuccess
    statusIsError.value = false
    showToast(copy.value.loginSuccess)
    setTimeout(() => emit('update:modelValue', false), 350)
  } catch (error) {
    statusIsError.value = true
    statusText.value = resolveErrorMessage(error)
    // 登录失败时不要清理用户正在输入的数据，只清除可能缓存的半态会话
    if (!session.value?.token) clearSession()
  } finally {
    submitting.value = false
  }
}

/**
 * Google credential 登录（与邮箱登录成功后会话字段一致）。
 *
 * @param credential - GIS 返回的 JWT
 */
async function performGoogleLogin(credential: string): Promise<void> {
  submitting.value = true
  statusText.value = ''
  try {
    const data = await postJson<{
      ok: boolean
      token: string
      refreshToken: string
      email: string
      username: string
      nickname: string
      avatar: string
      createdAt: string
      isSubscribed: boolean
    }>('/api/auth/google', {
      credential,
      keep: true,
      keepDays: 30,
    })
    setSession({
      token: data.token,
      refreshToken: data.refreshToken,
      email: data.email,
      username: data.username,
      nickname: data.nickname,
      avatar: data.avatar,
      createdAt: data.createdAt,
      isSubscribed: data.isSubscribed,
      ...resolveSessionKeepFields(),
    })
    statusText.value = copy.value.loginSuccess
    statusIsError.value = false
    showToast(copy.value.loginSuccess)
    setTimeout(() => emit('update:modelValue', false), 350)
  } catch (error) {
    statusIsError.value = true
    statusText.value = resolveErrorMessage(error)
    if (!session.value?.token) clearSession()
  } finally {
    submitting.value = false
  }
}

/**
 * GIS 回调：提交 credential 完成登录。
 *
 * @param credential - JWT id_token
 */
async function onGoogleCredential(credential: string): Promise<void> {
  if (!credential.trim()) return
  await performGoogleLogin(credential.trim())
}

function onOAuthSession(nextSession: AuthSession): void {
  setSession(nextSession)
  statusText.value = copy.value.loginSuccess
  statusIsError.value = false
  showToast(copy.value.loginSuccess)
}

/**
 * 调用后端注册接口。
 */
async function performRegister(): Promise<void> {
  submitting.value = true
  statusText.value = ''
  try {
    await postJson('/api/auth/register', {
      email: formModel.value.email,
      code: formModel.value.code,
      password: formModel.value.password,
    })
    statusText.value = copy.value.registerSuccess
    statusIsError.value = false
    showToast(copy.value.registerSuccess)
    onModeChange('login')
  } catch (error) {
    statusIsError.value = true
    statusText.value = resolveErrorMessage(error)
  } finally {
    submitting.value = false
  }
}

/**
 * 调用后端找回密码接口。
 */
async function performResetPassword(): Promise<void> {
  submitting.value = true
  statusText.value = ''
  try {
    await postJson('/api/auth/reset-password', {
      email: formModel.value.email,
      code: formModel.value.code,
      password: formModel.value.password,
    })
    statusText.value = copy.value.resetSuccess
    statusIsError.value = false
    showToast(copy.value.resetSuccess)
    onModeChange('login')
  } catch (error) {
    statusIsError.value = true
    statusText.value = resolveErrorMessage(error)
  } finally {
    submitting.value = false
  }
}

/**
 * 将异常映射为提示文案。
 *
 * @param err - 异常对象
 */
function resolveErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : ''
  if (raw.includes('invalid_email')) return copy.value.emailInvalid
  if (raw.includes('invalid_code')) return copy.value.invalidCode
  if (raw.includes('email_registered')) return copy.value.emailRegistered
  if (raw.includes('user_not_found')) return copy.value.userNotFound
  if (raw.includes('password_invalid')) return copy.value.passwordInvalid
  if (raw.includes('password_too_short')) return copy.value.passwordTooShort
  if (raw.includes('send_code_rate_limited')) return t('auth.sendCodeRateLimited')
  if (raw && !raw.startsWith('request_failed')) return raw
  return copy.value.requestFailed
}
</script>

<style scoped>
.editor-auth-toast {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  z-index: 1200;
  background: rgb(15 23 42 / 92%);
  color: #fff;
  border-radius: 999px;
  padding: 0.5rem 0.85rem;
  font-size: 0.82rem;
  box-shadow: 0 10px 30px rgb(2 6 23 / 25%);
}

.editor-auth-toast-enter-active,
.editor-auth-toast-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.editor-auth-toast-enter-from,
.editor-auth-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -6px);
}
</style>
